import { BaseService } from './BaseService';
import {
  CreateUpdateVeterinaryCommand,
  CreateUpdateVetContactModel,
  VeterinaryViewModel,
  VetsQueryResult,
  VetsMedicalSupplyViewModel
} from '../models/vets';
import { VeterinaryOrderViewModel } from '../models/injection';
import { PageableResult } from '../models/common';
import { LocationPosition, LocationsPageableQueryResult, LocationStable } from '../models/locations';
import { AnimalType } from '../models/animal';

export class VetService extends BaseService {
  getVeterinaries = async (page: number, size: number, searchText?: string): Promise<VetsQueryResult> => {
    const query = this.BuildQueryString({ page, size, searchText });

    const result = await this.Fetch(`/vets?${query}`, 'GET');

    return this.FromResult<VetsQueryResult>(result);
  };

  getVeterinary = async (id: string) => {
    const result = await this.Fetch(`/vets/${id}`, 'GET');

    return this.FromResult<VeterinaryViewModel>(result);
  };

  createVet = async (command: CreateUpdateVeterinaryCommand): Promise<string> => {
    const result = await this.Fetch('/vets', 'POST', JSON.stringify(command));

    return this.FromResult<string>(result);
  };

  updateVet = async (command: CreateUpdateVeterinaryCommand): Promise<string> => {
    const result = await this.Fetch('/vets', 'PUT', JSON.stringify(command));

    return this.FromResult<string>(result);
  };

  assignFarmToVet = async (farmId: string, veterinaryId: string): Promise<string> => {
    const command = { farmId, veterinaryId };

    const result = await this.Fetch('/vets/farm', 'PUT', JSON.stringify(command));

    return this.FromResult<string>(result);
  };

  unlinkFarmFromVet = async (farmId: string, veterinaryId: string): Promise<string> => {
    const command = { farmId, veterinaryId };
    const result = await this.Fetch('/vets/farm', 'DELETE', JSON.stringify(command));

    return this.FromResult<string>(result);
  };

  createVetContact = async (id: string, contact: CreateUpdateVetContactModel): Promise<string> => {
    const command = { id, contact };

    const result = await this.Fetch('/vets/contact', 'POST', JSON.stringify(command));

    return this.FromResult<string>(result);
  };

  deleteVetContact = async (id: string, veterinaryId: string): Promise<string> => {
    const command = { id, veterinaryId };

    const result = await this.Fetch('/vets/contact', 'DELETE', JSON.stringify(command));

    return this.FromResult<string>(result);
  };

  updateVetContact = async (id: string, contact: CreateUpdateVetContactModel): Promise<string> => {
    const command = { id, contact };

    const result = await this.Fetch('/vets/contact', 'PUT', JSON.stringify(command));

    return this.FromResult<string>(result);
  };

  getVetMedicalSupplyOrders = async (page: number, size: number): Promise<PageableResult<VeterinaryOrderViewModel>> => {
    const query = this.BuildQueryString({ page, size });

    const result = await this.Fetch(`/vets/orders?${query}`, 'GET');

    return this.FromResult<PageableResult<VeterinaryOrderViewModel>>(result);
  };

  getVetLocationsByQuery = async (
    page: number,
    size: number,
    number: string,
    room: string,
    pen: string,
    position: LocationPosition,
    stable: LocationStable,
    animalType: AnimalType
  ): Promise<LocationsPageableQueryResult> => {
    const query = this.BuildQueryString({ page, size, number, room, pen, position, stable, animalType });

    const result = await this.Fetch(`/vets/locations/search?${query}`, 'GET');

    return this.FromResult<LocationsPageableQueryResult>(result);
  };

  getVetMedicalSupplies = async (farmId: string): Promise<VetsMedicalSupplyViewModel[]> => {
    const query = this.BuildQueryString({ farmId });

    const result = await this.Fetch(`/vets/medical-supplies?${query}`, 'GET');

    return this.FromResult<VetsMedicalSupplyViewModel[]>(result);
  };

  createVetOralFeedInjection = async (farmSupplyId: string, locationId: string, dose: number): Promise<string[]> => {
    const command = JSON.stringify({ farmSupplyId, locationId, dose });

    const result = await this.Fetch('/vets/injections/oralfeed', 'POST', command);

    return this.FromResult<string[]>(result);
  };

  // Vaccination flow: same dose/supply logic as the oral feed, but the backend
  // filters the location's animals down to gilts only. Vaccination supplies are
  // injectable, so the Oral method requirement does not apply here.
  createVetVaccinationInjection = async (
    farmSupplyId: string,
    locationId: string,
    repeatDays: number,
    dose: number
  ): Promise<string[]> => {
    const command = JSON.stringify({ farmSupplyId, locationId, repeatDays, dose });

    const result = await this.Fetch('/vets/injections/vaccination', 'POST', command);

    return this.FromResult<string[]>(result);
  };
}

export default VetService;
